<script setup lang="ts">
import DrawingInfo from "./DrawingInfo.vue";
import { computed, ref, toRaw } from "vue";
import { Logger } from "@/logger";
import { useToast } from "@/composable/useToast";
import { useDeviceState } from "@/stores/DeviceState";
import DwgIcon from "@/components/UI/icons/DwgIcon.vue";
import MapIcon from "@/components/UI/icons/MapIcon.vue";
import type { DRLocalDrawingMeta, DRDrawing } from "@/types";
import { syncer } from "@/repositories/Syncer";
import { useNavigate } from "@/composable/useNavigate";
import type { ItemDisplayMode } from "@/stores/Settings";
import DrawingActionsDropdown from "./DrawingActionsDropdown.vue";
import { useDates } from "@/composable/useDates";
import { useTranslation } from "i18next-vue";
import DRDisplayItem from "@/components/UI/items/DRDisplayItem.vue";
import { useDrawingStore } from "@/stores/fileStore";
import { get } from "lodash";
import DRUserAvatar from "@/components/UI/DRUserAvatar.vue";
import ItemIndicator from "@/components/UI/items/ItemIndicator.vue";
import DrawingBranchesList from "../DrawingBranches/DrawingBranchesList.vue";

const { t } = useTranslation();
const { formatDistanceToNow } = useDates();
const DrawingStore = useDrawingStore();

const device = useDeviceState();
const isOnline = computed(() => device.isOnline);

const props = withDefaults(
  defineProps<{
    selectMode?: boolean;
    drawing: DRDrawing;
    disableSelect?: boolean;
    display?: ItemDisplayMode;
    selected?: boolean;
    /** Masque la pastille ouvrage (liste déjà filtrée sur un ouvrage) */
    hideDiagnostic?: boolean;
  }>(),
  {
    disableSelect: false,
    selected: false,
    display: "card",
    selectMode: false,
    hideDiagnostic: false,
  },
);

const drawingId = computed(() => props.drawing.id);

const localDrawing = computed<DRLocalDrawingMeta | undefined>(() => {
  const id = drawingId.value;
  return DrawingStore.localDrawings?.find((ld) => ld.id === id) || undefined;
});

const { showSuccess, showSecondary, showDanger } = useToast();

const emits = defineEmits<{
  (e: "toggle-select"): void;
  (e: "keep-focus-on"): void;
}>();

const toggleSelect = () => {
  emits("toggle-select");
};

const isMakingLocal = ref(false);

const { navigateCanva: navigate } = useNavigate();

async function open() {
  Logger.info(
    `DrawingItem.vue : opening ${props.drawing.name} ${props.drawing.id}`,
  );
  if (localDrawing.value) {
    if (
      localDrawing.value.lastRevision?.id !== props.drawing.lastRevision?.id
    ) {
      showSecondary(t("dashboard:branches.mismatch.notification"));
      return;
    }
    navigate(localDrawing.value.id);
    return;
  }

  if (!device.isOnline) return;

  makeLocal().then(() => {
    navigate(drawingId.value);
  });
}

async function makeLocal() {
  if (isMakingLocal.value) return;
  isMakingLocal.value = true;

  try {
    if (localDrawing.value) {
      throw new Error(
        `DrawingItem.vue : drawing ${localDrawing.value.id} is already local`,
      );
    }

    await syncer.syncNewDrawing(toRaw(props.drawing));
    showSuccess("toast:drawing.make-local.success", {
      name: props.drawing.name,
    });
  } catch (e) {
    Logger.error(
      `DrawingItem.vue : could not make drawing local ${drawingId.value},  `,
    );
    console.error(e);
    showDanger("toast:drawing.make-local.error");
  } finally {
    isMakingLocal.value = false;
  }
}

const handleClick = () => {
  if (props.selectMode) toggleSelect();
  else open();
};

const retrySync = async () => {
  if (!localDrawing.value) {
    showDanger("toast:drawing.not-available");
    return;
  }
  await DrawingStore.retrySyncDrawing(localDrawing.value.id);
};

const lastSaveText = computed(() => {
  const savedAt = props.drawing?.lastRevision?.savedAt;
  if (savedAt) return `${t("lastSaved")} ${formatDistanceToNow(savedAt)}`;
  return t("new");
});

const showBranches = ref(false);
const extended = computed({
  get: () => showBranches.value && !!isOnline.value,
  set: (value: boolean) => {
    showBranches.value = value;
  },
});
</script>

<template>
  <DRDisplayItem
    v-if="drawing"
    :display="display"
    @click="handleClick"
    :title="drawing.name"
    :selected="selected"
    v-model:extended="extended"
  >
    <template #icon="{ size }">
      <DwgIcon :size="size * 1.2" />
    </template>

    <template #subtitle>
      <div class="d-flex flex-row text-grey align-items-center gap-1">
        <DRUserAvatar
          v-if="drawing.createdBy"
          :size="16"
          :user="drawing.createdBy"
          :tooltip="get(drawing, 'createdBy.full_name', 'Unknown User')"
        />
        <div class="text-truncate">
          {{
            `${t("addedBy")} ${drawing.createdBy?.full_name} • ${lastSaveText}`
          }}
        </div>
      </div>
    </template>
    <!-- Groupés : en mode carte le slot est étalé par un justify-content-between,
         sans conteneur la pastille ouvrage se retrouverait isolée au milieu. -->
    <template #more>
      <span class="more-group">
        <el-button
          v-if="isOnline && drawing.branchesCount && drawing.branchesCount > 1"
          size="small"
          round
          type="info"
          plain
          class="ps-1 pe-1"
          @click.stop="showBranches = !showBranches"
        >
          <span class="mx-1">{{
            $t("dashboard:branches.count", { count: drawing.branchesCount })
          }}</span>
          <el-icon>
            <i :class="showBranches ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </el-icon>
        </el-button>

        <!-- Ouvrage auquel ce plan est rattaché, après le badge des versions -->
        <ItemIndicator
          v-if="drawing.diagnostic && !hideDiagnostic"
          :tooltip="
            t('dashboard:drawings.linkedDiagnostic', {
              name:
                drawing.diagnostic.name || t('report:diagnostics.item.noname'),
            })
          "
        >
          <template #icon="{ size }">
            <MapIcon :size="size" />
          </template>
        </ItemIndicator>
      </span>
    </template>

    <template #actions>
      <DrawingActionsDropdown
        :drawing="drawing"
        :local-data="localDrawing"
        :selected="selected"
        :disable-select="disableSelect"
        :hide-diagnostic="hideDiagnostic"
        @toggle-select="toggleSelect()"
      />
    </template>
    <template #append1>
      <DrawingInfo
        @make-local="makeLocal()"
        @retry-sync="retrySync()"
        :local-drawing="localDrawing"
        :drawing="drawing"
        :spinning="isMakingLocal"
      />
    </template>
    <template v-if="extended" #extended>
      <DrawingBranchesList
        @click.stop
        :drawing="drawing"
        @switched-branch="$emit('keep-focus-on')"
      />
    </template>
  </DRDisplayItem>
</template>

<style lang="scss" scoped>
/* Badge des versions puis pastille ouvrage, collés en bout de ligne */
.more-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>

<style lang="scss">
[data-bs-theme="dark"] .el-button.el-button--info.is-plain {
  --el-button-bg-color: transparent;
  --el-button-border-color: var(--bs-border-color);
  --el-button-text-color: var(--el-text-color-regular);
  --el-button-hover-bg-color: rgba(255, 255, 255, 0.08);
  --el-button-hover-border-color: var(--el-text-color-regular);
  --el-button-hover-text-color: var(--el-text-color-regular);
}
</style>
